'use client'

import * as RadixTabs from '@radix-ui/react-tabs'
import type { SbBlokData } from '@storyblok/react'
import { StoryblokComponent, storyblokEditable } from '@storyblok/react'
import { Heading } from 'ui/src/components/Heading/Heading'
import { xStack } from 'ui/src/patterns/stack'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import type { SbBaseBlockProps, ExpectedBlockType } from '@/services/storyblok/storyblok'
import { filterByBlockType } from '@/services/storyblok/Storyblok.helpers'
import { wrapper, title, tabListWrapper, tabTrigger } from './TabsBlock.css'

type TabBlockFields = {
  title: string
  body: Array<SbBlokData>
}

type TabBlockProps = SbBaseBlockProps<TabBlockFields>

type TabsBlockProps = SbBaseBlockProps<{
  items: ExpectedBlockType<TabBlockProps>
  heading?: string
}>

export function TabsBlock({ blok }: TabsBlockProps) {
  const tabBlocks = filterByBlockType(blok.items, TabBlock.blockName)
  const firstTabValue = tabBlocks[0]?._uid

  return (
    <div className={wrapper} {...storyblokEditable(blok)}>
      {blok.heading && (
        <Heading className={title} as="h2" variant={{ _: 'standard.24', lg: 'standard.32' }} align="center">
          {blok.heading}
        </Heading>
      )}
      <RadixTabs.Tabs defaultValue={firstTabValue}>
        <GridLayout.Root>
          <GridLayout.Content className={tabListWrapper} width="2/3" align="center">
            <RadixTabs.TabsList className={xStack({ gap: 'xxs', overflowX: 'auto' })}>
              {tabBlocks.map((tabBlock) => {
                const tabId = tabBlock._uid || tabBlock.title
                return (
                  <RadixTabs.Trigger className={tabTrigger} key={tabId} value={tabId}>
                    {tabBlock.title}
                  </RadixTabs.Trigger>
                )
              })}
            </RadixTabs.TabsList>
          </GridLayout.Content>

          <GridLayout.Content width="1/2" align="center">
            {tabBlocks.map((tabBlock) => (
              <TabBlock key={tabBlock._uid || tabBlock.title} blok={tabBlock} />
            ))}
          </GridLayout.Content>
        </GridLayout.Root>
      </RadixTabs.Tabs>
    </div>
  )
}

function TabBlock({ blok }: TabBlockProps) {
  return (
    <RadixTabs.TabsContent value={blok._uid || blok.title}>
      {blok.body.map((nestedBlock) => (
        <StoryblokComponent key={nestedBlock._uid} blok={nestedBlock} nested={true} />
      ))}
    </RadixTabs.TabsContent>
  )
}
TabBlock.blockName = 'tab'
